// web/src/pages/admin/lookups/InvoiceStatusesPage.tsx
//
// I-15 — Admin CRUD for invoice-status codes (`/api/admin/invoice-statuses`).
//
// All members of `InvoiceStatusCodes.All` are foundation rows: they're hard
// dependencies of the lifecycle state machine, the composer, and the
// statement composer. Mirror the backend constant here — see
// src/Allmark.Domain/InvoicesPayments/InvoiceStatusCodes.cs.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Lock } from "lucide-react";
import { z } from "zod";
import {
  listInvoiceStatuses,
  createInvoiceStatus,
  updateInvoiceStatus,
  deleteInvoiceStatus,
  type InvoiceStatusDto,
} from "@/lib/invoices-payments";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailModal } from "@/components/detail-modal";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

// Mirror of Allmark.Domain.InvoicesPayments.InvoiceStatusCodes.All.
// Keep in sync with src/Allmark.Domain/InvoicesPayments/InvoiceStatusCodes.cs.
export const FOUNDATION_INVOICE_STATUSES: ReadonlySet<string> = new Set([
  "Draft",
  "Sent",
  "PartPaid",
  "Paid",
  "OverPaid",
  "Overdue",
  "Cancelled",
]);

const schema = z.object({
  invoiceStatusCode: z.string().min(1).max(40),
  description: z.string().min(1).max(200),
  sortOrder: z.number().int().nullable(),
});
type Form = z.infer<typeof schema>;

export function InvoiceStatusesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<InvoiceStatusDto | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ["invoice-statuses"],
    queryFn: () => listInvoiceStatuses(),
  });

  const upsert = useMutation({
    mutationFn: (form: Form) => editing
      ? updateInvoiceStatus(editing.invoiceStatusCode, {
          description: form.description,
          sortOrder: form.sortOrder,
        })
      : createInvoiceStatus({
          invoiceStatusCode: form.invoiceStatusCode,
          description: form.description,
          sortOrder: form.sortOrder,
        }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["invoice-statuses"] });
      setEditing(null);
      setCreating(false);
    },
  });

  const del = useMutation({
    mutationFn: (code: string) => deleteInvoiceStatus(code),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["invoice-statuses"] }),
  });

  const cols: DataGridColumn<InvoiceStatusDto>[] = [
    {
      id: "code",
      header: "Code",
      accessor: (r) => (
        <span className="inline-flex items-center gap-1">
          {FOUNDATION_INVOICE_STATUSES.has(r.invoiceStatusCode) && (
            <Lock aria-label="Foundation row (delete locked)" className="size-3 text-muted-foreground" />
          )}
          {r.invoiceStatusCode}
        </span>
      ),
    },
    { id: "desc", header: "Description", accessor: (r) => r.description },
    { id: "sort", header: "Sort", accessor: (r) => r.sortOrder ?? "—", align: "right" },
    {
      id: "actions",
      header: "",
      accessor: (r) => {
        const isFoundation = FOUNDATION_INVOICE_STATUSES.has(r.invoiceStatusCode);
        return (
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Edit ${r.invoiceStatusCode}`}
              onClick={() => setEditing(r)}
            >
              Edit
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Delete ${r.invoiceStatusCode}`}
              disabled={isFoundation || del.isPending}
              onClick={() => {
                if (!isFoundation) del.mutate(r.invoiceStatusCode);
              }}
            >
              Delete
            </Button>
          </div>
        );
      },
      align: "right",
    },
  ];

  const drawerOpen = creating || editing !== null;
  const defaultValues: Form = editing
    ? {
        invoiceStatusCode: editing.invoiceStatusCode,
        description: editing.description,
        sortOrder: editing.sortOrder,
      }
    : { invoiceStatusCode: "", description: "", sortOrder: null };

  return (
    <ListPage backTo="/admin"
      title="Invoice statuses"
      primaryAction={
        <IntentButton onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> Add Status</IntentButton>
      }
    >
      <DataGrid
        rows={list.data ?? []}
        columns={cols}
        rowKey={(r) => r.invoiceStatusCode}
        totalRows={list.data?.length ?? 0}
        pageIndex={0}
        pageSize={Math.max(50, list.data?.length ?? 0)}
        loading={list.isLoading}
        onPageChange={() => undefined}
      />
      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
        title={editing ? "Edit invoice status" : "New invoice status"}
      >
        <EntityForm<Form>
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              <div>
                <Label htmlFor="is-code">Code</Label>
                <Input id="is-code" disabled={editing !== null} {...form.register("invoiceStatusCode")} />
              </div>
              <div>
                <Label htmlFor="is-desc">Description</Label>
                <Input id="is-desc" {...form.register("description")} />
              </div>
              <div>
                <Label htmlFor="is-sort">Sort order</Label>
                <Input
                  id="is-sort"
                  type="number"
                  {...form.register("sortOrder", {
                    setValueAs: (v: string) => (v === "" ? null : Number(v)),
                  })}
                />
              </div>
              <Button type="submit">Save</Button>
            </div>
          )}
        </EntityForm>
      </DetailModal>
    </ListPage>
  );
}
